<template>
    <div
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
        role="dialog"
        @click.self="$emit('close')"
    >
        <div class="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div class="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                <h3 class="text-lg font-semibold text-slate-700 dark:text-white">
                    Revalidação biométrica
                </h3>
                <button
                    type="button"
                    class="text-slate-400 hover:text-slate-600 dark:hover:text-white"
                    @click="$emit('close')"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <div class="px-6 py-4 space-y-4 text-sm text-slate-600 dark:text-slate-300">
                <div v-if="loading" class="flex items-center justify-center py-8">
                    <svg class="w-8 h-8 text-indigo-500 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                </div>

                <template v-else-if="data">
                    <section>
                        <h4 class="font-semibold text-slate-700 dark:text-white mb-2">Usuário</h4>
                        <dl class="grid grid-cols-2 gap-x-4 gap-y-1">
                            <dt class="text-slate-400">Nome</dt>
                            <dd>{{ data.name }}</dd>
                            <dt class="text-slate-400">Email</dt>
                            <dd>{{ data.email }}</dd>
                            <dt class="text-slate-400">Tipo</dt>
                            <dd>{{ data.type === 'company' ? 'PJ' : 'PF' }}</dd>
                            <dt class="text-slate-400">CPF</dt>
                            <dd>{{ data.cpf || '—' }}</dd>
                            <dt class="text-slate-400">CNPJ</dt>
                            <dd>{{ data.cnpj || '—' }}</dd>
                            <dt class="text-slate-400">Aprovado</dt>
                            <dd>{{ data.is_approved ? 'Sim' : 'Não' }}</dd>
                        </dl>
                    </section>

                    <section>
                        <h4 class="font-semibold text-slate-700 dark:text-white mb-2">Dados necessários</h4>
                        <ul class="space-y-1">
                            <li :class="data.data_check.effective_cpf ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'">
                                {{ data.data_check.effective_cpf ? '✅' : '❌' }}
                                CPF do titular/sócio
                                <span v-if="data.data_check.effective_cpf" class="text-slate-400 ml-1">
                                    ({{ data.data_check.effective_cpf }} via {{ data.data_check.cpf_source }})
                                </span>
                            </li>
                            <li :class="data.data_check.effective_birth ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'">
                                {{ data.data_check.effective_birth ? '✅' : '❌' }}
                                Data de nascimento
                                <span v-if="data.data_check.effective_birth" class="text-slate-400 ml-1">
                                    ({{ data.data_check.effective_birth }} via {{ data.data_check.birth_source }})
                                </span>
                            </li>
                            <li :class="data.data_check.effective_name ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'">
                                {{ data.data_check.effective_name ? '✅' : '❌' }}
                                Nome para biometria: {{ data.data_check.effective_name || '—' }}
                            </li>
                        </ul>
                    </section>

                    <section>
                        <h4 class="font-semibold text-slate-700 dark:text-white mb-2">Estado do fluxo</h4>
                        <dl class="grid grid-cols-2 gap-x-4 gap-y-1">
                            <dt class="text-slate-400">Estado atual</dt>
                            <dd>
                                <span :class="flowBadge(data.flow_state)" class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium">
                                    {{ flowLabel(data.flow_state) }}
                                </span>
                            </dd>
                            <dt class="text-slate-400">biometric_required_at</dt>
                            <dd>{{ formatDate(data.biometric_required_at) }}</dd>
                            <dt class="text-slate-400">biometric_validated_at</dt>
                            <dd>{{ formatDate(data.biometric_validated_at) }}</dd>
                            <dt class="text-slate-400">biometric_block_reason</dt>
                            <dd>{{ data.biometric_block_reason || '—' }}</dd>
                            <dt class="text-slate-400">Tentativas</dt>
                            <dd>{{ data.biometric_attempts }}</dd>
                        </dl>
                    </section>

                    <section v-if="data.force_overlay_blockers && data.force_overlay_blockers.length">
                        <div class="rounded-md bg-yellow-50 dark:bg-yellow-900/20 px-3 py-2 text-yellow-700 dark:text-yellow-200 text-xs">
                            <strong>Não é possível forçar overlay:</strong>
                            <ul class="list-disc ml-4 mt-1">
                                <li v-for="reason in data.force_overlay_blockers" :key="reason">
                                    {{ blockerLabel(reason) }}
                                </li>
                            </ul>
                        </div>
                    </section>
                </template>
            </div>

            <div class="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-2">
                <button
                    type="button"
                    class="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white"
                    @click="$emit('close')"
                >
                    Fechar
                </button>
                <button
                    v-if="data"
                    type="button"
                    :disabled="!data.can_force_overlay || forcing"
                    :class="data.can_force_overlay ? 'bg-indigo-500 hover:bg-indigo-400 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'"
                    class="px-4 py-2 text-sm font-semibold rounded-md shadow"
                    @click="forceOverlay"
                >
                    <span v-if="forcing">Forçando...</span>
                    <span v-else>Forçar overlay</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        userId: { type: Number, required: true },
    },
    emits: ['close', 'forced'],
    data() {
        return {
            loading: false,
            forcing: false,
            data: null,
        };
    },
    mounted() {
        this.fetch();
    },
    methods: {
        fetch() {
            this.loading = true;
            axios.get(`/api/kyc/biometric-revalidation/${this.userId}`)
                .then(({ data }) => {
                    this.data = data.data;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        forceOverlay() {
            if (!this.data?.can_force_overlay || this.forcing) return;
            this.forcing = true;
            axios.post(`/api/kyc/biometric-revalidation/${this.userId}/force-overlay`)
                .then(() => {
                    this.$toast?.success('Overlay forçado com sucesso.');
                    this.$emit('forced');
                })
                .catch((error) => {
                    const message = error?.response?.data?.message ?? 'Erro ao forçar overlay.';
                    this.$toast?.error(message);
                })
                .finally(() => {
                    this.forcing = false;
                });
        },
        flowLabel(state) {
            return {
                validated: 'Validado',
                pending: 'Aguardando biometria',
                blocked: 'Bloqueado',
                not_started: 'Não iniciou',
            }[state] ?? state;
        },
        flowBadge(state) {
            return {
                validated: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
                pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
                blocked: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
                not_started: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
            }[state] ?? 'bg-gray-100 text-gray-800';
        },
        blockerLabel(reason) {
            return {
                feature_flag_off: 'Feature flag biometric_revalidation_enforcement está desativada.',
                already_validated: 'Usuário já passou pela revalidação biométrica.',
            }[reason] ?? reason;
        },
        formatDate(value) {
            if (!value) return '—';
            try {
                return new Date(value).toLocaleString('pt-BR');
            } catch {
                return value;
            }
        },
    },
};
</script>
